'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef } from 'react';
import { Button } from '@extern/ui/button';
import { Checkbox } from '@extern/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputPassword, InputPasswordTooltip } from '@extern/ui/input-password';
import { Separator } from '@extern/ui/separator';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { AuthCard } from '@/app/(auth)/_partials/auth-card';
import { AuthGoogleButton } from '@/app/(auth)/_partials/auth-google-button';
import { AuthLastUsedButtonWrap } from '@/app/(auth)/_partials/auth-last-used-badge';
import { emailSchema, passwordSchema } from '@/infra/schemas/auth';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { useLastAuthenticationStrategy } from '../../_hooks/use-last-authentication-strategy';
import { useSignUp } from '../_hooks/use-sign-up';
import { useSignUpFlowStore } from '../_hooks/use-sign-up-flow-store';
import { prefilledEmail, prefilledNewsletter } from '../_utils/prefilled-email';
const FormSchema = z.object({
  firstName: z.string().min(2, {
    message: 'First name is required'
  }),
  lastName: z.string().min(2, {
    message: 'Last name is required'
  }),
  emailAddress: emailSchema,
  password: passwordSchema
});
export function SignUpFormControl() {
  const $ = _c(67);
  const searchParams = useSearchParams();
  const {
    onSignUpSubmit,
    onSignUpWithGoogle,
    isPending,
    isGoogleLoading
  } = useSignUp();
  const {
    trackSignUpChoiceClicked
  } = useAuthEvents();
  const lastAuthenticationStrategy = useLastAuthenticationStrategy();
  const newsletter = useSignUpFlowStore(_temp);
  const onChangeFlow = useSignUpFlowStore(_temp2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = zodResolver(FormSchema);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== searchParams) {
    t1 = prefilledEmail(searchParams);
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      resolver: t0,
      defaultValues: {
        firstName: "",
        lastName: "",
        emailAddress: t1,
        password: ""
      },
      mode: "onChange"
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const form = useForm(t2);
  const appliedNewsletterChoice = useRef(false);
  let t3;
  let t4;
  if ($[5] !== onChangeFlow || $[6] !== searchParams) {
    t3 = () => {
      if (appliedNewsletterChoice.current) {
        return;
      }
      appliedNewsletterChoice.current = true;
      const choice = prefilledNewsletter(searchParams);
      if (choice !== null) {
        onChangeFlow({
          newsletter: choice
        });
      }
    };
    t4 = [searchParams, onChangeFlow];
    $[5] = onChangeFlow;
    $[6] = searchParams;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[9] !== newsletter || $[10] !== onSignUpSubmit || $[11] !== trackSignUpChoiceClicked) {
    t5 = function handleSubmit(values) {
      trackSignUpChoiceClicked("email", newsletter);
      onSignUpSubmit({
        ...values,
        newsletter
      });
    };
    $[9] = newsletter;
    $[10] = onSignUpSubmit;
    $[11] = trackSignUpChoiceClicked;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleSubmit = t5;
  let t6;
  if ($[13] !== form || $[14] !== handleSubmit) {
    t6 = form.handleSubmit(handleSubmit);
    $[13] = form;
    $[14] = handleSubmit;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AuthCard.Header><AuthCard.Title>Start Your Career Journey with Extern</AuthCard.Title><AuthCard.Description>Gain access to real-world projects, hands-on experience, and a community of professionals.</AuthCard.Description></AuthCard.Header>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[17] !== form.control) {
    t8 = <FormField control={form.control} name="firstName" render={_temp3} />;
    t9 = <FormField control={form.control} name="lastName" render={_temp4} />;
    t10 = <FormField control={form.control} name="emailAddress" render={_temp5} />;
    t11 = <FormField control={form.control} name="password" render={_temp6} />;
    $[17] = form.control;
    $[18] = t10;
    $[19] = t11;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[22] !== onChangeFlow) {
    t12 = checked => onChangeFlow({
      newsletter: checked === true
    });
    $[22] = onChangeFlow;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== newsletter || $[25] !== t12) {
    t13 = <div className="flex gap-2"><Checkbox checked={newsletter} label="I want exclusive invites and opportunities at Extern" onCheckedChange={t12} /></div>;
    $[24] = newsletter;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t13 || $[30] !== t8 || $[31] !== t9) {
    t14 = <AuthCard.Content><div className="grid gap-4">{t8}{t9}{t10}{t11}{t13}</div></AuthCard.Content>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t13;
    $[30] = t8;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <br />;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Link className="underline" href="https://www.extern.com/terms-of-service" target="_blank">Terms</Link>;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="text-center font-medium text-gray-dark text-xs">By signing up, you agree to{t15}Extern's{" "}{t16}{" "}and{" "}<Link className="underline" href="https://www.extern.com/privacy-policy" target="_blank">Privacy Policy</Link>.</div>;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== isPending) {
    t18 = <Button className="w-full" disabled={isPending} isLoading={isPending} type="submit">Sign up with Email</Button>;
    $[36] = isPending;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== lastAuthenticationStrategy || $[39] !== t18) {
    t19 = <AuthLastUsedButtonWrap matchStrategy="password" strategy={lastAuthenticationStrategy}>{t18}</AuthLastUsedButtonWrap>;
    $[38] = lastAuthenticationStrategy;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="relative flex w-full items-center justify-center p-3"><Separator /><span className="absolute mx-auto bg-white px-4 text-gray-dark text-sm">or</span></div>;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== newsletter || $[43] !== onSignUpWithGoogle || $[44] !== trackSignUpChoiceClicked) {
    t21 = () => {
      trackSignUpChoiceClicked("google", newsletter);
      onSignUpWithGoogle(newsletter).catch(_temp7);
    };
    $[42] = newsletter;
    $[43] = onSignUpWithGoogle;
    $[44] = trackSignUpChoiceClicked;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== isGoogleLoading || $[47] !== t21) {
    t22 = <AuthGoogleButton isLoading={isGoogleLoading} label="Sign up with Google" onClick={t21} />;
    $[46] = isGoogleLoading;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== searchParams) {
    t23 = getAllowlistedSearchParams(searchParams);
    $[49] = searchParams;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  const t24 = `/sign-in?${t23}`;
  let t25;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="text-blue-dark hover:underline">Sign in</span>;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t24) {
    t26 = <Text body="lg" className="text-center font-medium">Already have an account? <Link href={t24}>{t25}</Link></Text>;
    $[52] = t24;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t19 || $[55] !== t22 || $[56] !== t26) {
    t27 = <AuthCard.Footer>{t17}{t19}{t20}{t22}{t26}</AuthCard.Footer>;
    $[54] = t19;
    $[55] = t22;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t14 || $[59] !== t27) {
    t28 = <AuthCard>{t7}{t14}{t27}</AuthCard>;
    $[58] = t14;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t28 || $[62] !== t6) {
    t29 = <form onSubmit={t6}>{t28}</form>;
    $[61] = t28;
    $[62] = t6;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== form || $[65] !== t29) {
    t30 = <Form {...form}>{t29}</Form>;
    $[64] = form;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  return t30;
}
function _temp7() {}
function _temp6(t0) {
  const {
    field: field_2
  } = t0;
  return <FormItem><FormLabel className="flex items-center gap-2">Create a password<InputPasswordTooltip value={field_2.value} /></FormLabel><FormControl><InputPassword autoCapitalize="none" autoComplete="password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="Type in your password" type="password" {...field_2} /></FormControl><FormMessage /></FormItem>;
}
function _temp5(t0) {
  const {
    field: field_1
  } = t0;
  return <FormItem><FormLabel>Email</FormLabel><FormControl><Input placeholder="email@service.com" {...field_1} /></FormControl><FormMessage /></FormItem>;
}
function _temp4(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel>Last name</FormLabel><FormControl><Input placeholder="Last name" {...field_0} /></FormControl><FormMessage /></FormItem>;
}
function _temp3(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel>First name</FormLabel><FormControl><Input placeholder="First name" {...field} /></FormControl><FormMessage /></FormItem>;
}
function _temp2(s_0) {
  return s_0.onChange;
}
function _temp(s) {
  return s.newsletter;
}